/**
 * Image size preset picker: one card per print footprint, each with a
 * blocky schematic of the USER'S page shape (grey page, solid block where
 * the image will sit, faint text lines, dashed gutter on the spread
 * card), the physical print size, and the upfront per-image cost. The
 * footprint → model/size/cost mapping is the shared resolver, so the chip
 * quotes exactly what the API will log.
 */
import type { FormatPreset } from '@historyjki/shared';
import {
  IMAGE_SIZE_PRESETS,
  formatPageInches,
  resolveGenerationSpec,
  type ImageSizePreset,
  type ImageSizePresetDef,
} from '@historyjki/shared';

/** "~$0.04" — the app-wide phrasing for estimated generation cost. */
export function formatApproxUsd(usd: number): string {
  return `~$${usd.toFixed(2)}`;
}

const SCHEMATIC_H = 44;
const PAD = 3;

/** Blocky page-and-image schematic for one preset, drawn to the actual
 *  book format's aspect so "half page" looks like half of THIS book. */
function PresetSchematic({
  def,
  format,
}: {
  def: ImageSizePresetDef;
  format: FormatPreset | null;
}) {
  const { w, h } = formatPageInches(format);
  const pageH = SCHEMATIC_H - PAD * 2;
  const pageW = pageH * (w / h);
  const pages = def.pageW > 1 ? 2 : 1;
  const W = pages * pageW + PAD * 2;

  // Mirror the resolver's footprint rule: edge-to-edge spans the page
  // axes; floating spot art is square on the page's short side.
  const shortSide = Math.min(pageW, pageH);
  const blockW = def.pageW * (def.edgeToEdge ? pageW : shortSide);
  const blockH = def.pageH * (def.edgeToEdge ? pageH : shortSide);
  // Edge-to-edge footprints anchor to the cut edges (top/left); floating
  // spots center on the page.
  const blockX = def.edgeToEdge ? PAD : PAD + (pages * pageW - blockW) / 2;
  const blockY = def.edgeToEdge ? PAD : PAD + (pageH - blockH) * 0.42;

  // Faint text lines in the biggest leftover region: below a wide block,
  // beside a tall one. Full-coverage footprints have no room for text.
  const lines: Array<{ x: number; y: number, w: number }> = [];
  const lineGap = 4.5;
  if (def.pageH < 0.95 && def.pageW >= 0.95) {
    const freeTop = blockY + blockH + 4;
    for (let y = freeTop; y + 2 <= PAD + pageH - 2; y += lineGap) {
      lines.push({ x: PAD + 3, y, w: pages * pageW - 6 });
    }
  } else if (def.pageW < 0.95 && def.pageH >= 0.95 && def.edgeToEdge) {
    const freeLeft = blockX + blockW + 4;
    for (let y = PAD + 5; y + 2 <= PAD + pageH - 5; y += lineGap) {
      lines.push({ x: freeLeft, y, w: PAD + pageW - freeLeft - 3 });
    }
  } else if (!def.edgeToEdge) {
    const freeTop = blockY + blockH + 3;
    for (let y = freeTop; y + 2 <= PAD + pageH - 2; y += lineGap) {
      lines.push({ x: PAD + 3, y, w: pageW - 6 });
    }
  }

  return (
    <svg
      className="sizepick__svg"
      viewBox={`0 0 ${W} ${SCHEMATIC_H}`}
      height={SCHEMATIC_H}
      aria-hidden
    >
      {Array.from({ length: pages }, (_, i) => (
        <rect
          key={i}
          className="sizepick__page"
          x={PAD + i * pageW}
          y={PAD}
          width={pageW}
          height={pageH}
        />
      ))}
      <rect
        className="sizepick__block"
        x={blockX}
        y={blockY}
        width={blockW}
        height={blockH}
        rx={def.edgeToEdge ? 0 : 1.5}
      />
      {pages === 2 && (
        <line
          className="sizepick__gutter"
          x1={PAD + pageW}
          y1={PAD}
          x2={PAD + pageW}
          y2={PAD + pageH}
        />
      )}
      {lines.map((l, i) => (
        <rect key={i} className="sizepick__line" x={l.x} y={l.y} width={l.w} height={1.6} rx={0.8} />
      ))}
    </svg>
  );
}

interface SizePresetPickerProps {
  formatPreset: FormatPreset | null;
  value: ImageSizePreset;
  onChange: (value: ImageSizePreset) => void;
}

export function SizePresetPicker({ formatPreset, value, onChange }: SizePresetPickerProps) {
  return (
    <div className="sizepick" role="radiogroup" aria-label="Image size on the page">
      {IMAGE_SIZE_PRESETS.map((def) => {
        const spec = resolveGenerationSpec(def.value, formatPreset);
        const cm = `≈ ${Math.round(spec.printWidthCm)} × ${Math.round(spec.printHeightCm)} cm`;
        return (
          <button
            key={def.value}
            type="button"
            role="radio"
            aria-checked={value === def.value}
            className={`sizepick__card ${value === def.value ? 'sizepick__card--on' : ''}`}
            title={`${def.hint} — prints sharp up to ${cm}`}
            onClick={() => onChange(def.value)}
            data-testid={`size-preset-${def.value}`}
          >
            <PresetSchematic def={def} format={formatPreset} />
            <span className="sizepick__label">{def.label}</span>
            <span className="sizepick__meta">{cm}</span>
            <span className="sizepick__cost">{formatApproxUsd(spec.estCostUsd)}</span>
          </button>
        );
      })}
    </div>
  );
}
